import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Palette, Save, Loader2, Type } from "lucide-react";
import { NoAccess } from "@/components/NoAccess";
import { SettingsSection } from "@/components/SettingsSection";
import { BrandFont, loadFontStylesheet } from "@/components/BrandFont";

const PRESETS = [["", "Redline red"], ["#1B7F4B", "Forest"], ["#1E40AF", "Navy"], ["#7C3AED", "Violet"], ["#B45309", "Amber"], ["#0F766E", "Teal"], ["#191614", "Ink"]];
const inputCls = "w-full min-w-0 rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 bg-white";

// Company console → Appearance: the Redline console's own font + accent (super admin). The font
// is also the default for every community that left its font on "Ask Redline standard".
export default function Appearance() {
  const { role } = useDash();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ font: "default", accent: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/platform/appearance").then((r) => { setData(r.data); setForm({ font: r.data.font || "default", accent: r.data.color || "" }); }).catch(() => toast.error("Couldn't load the platform appearance"));
  }, []);
  useEffect(() => { (data?.font_options || []).forEach((f) => loadFontStylesheet(f.google)); }, [data?.font_options]);

  if (role !== "super_admin") return <NoAccess page="Appearance" hint="The platform look is set by the Ask Redline owner." />;
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const font = (data.font_options || []).find((f) => f.key === form.font) || data.font_options[0];
  const upd = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/platform/appearance", form);
      setData((d) => ({ ...d, ...r.data })); setDirty(false);
      window.dispatchEvent(new CustomEvent("platform-appearance-changed", { detail: r.data }));
      toast.success(`Console now wears ${r.data.font_label}${r.data.color ? ` · ${r.data.color}` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="appearance-page">
      <div>
        <PageTitle title={<><Palette className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Appearance</>} about="How the Redline console looks for you and your staff. Communities keep their own brand; the font you pick here is also the default for any community that hasn't chosen one." />
      </div>

      <SettingsSection id="platform-font" icon={Type} title="Console font" defaultOpen
        badges={[{ text: data.font_label || font?.label, on: form.font !== "default" }]}
        summary="The typeface of the company console and the default face for communities on “Ask Redline standard”."
        headerRight={dirty ? <button type="button" data-testid="appearance-save" onClick={save} disabled={saving} className="rounded-full bg-ink text-white px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</button> : null}>
        <div className="grid gap-3 sm:grid-cols-2">
          {(data.font_options || []).map((f) => {
            const on = f.key === form.font;
            return (
              <button key={f.key} type="button" data-testid={`appearance-font-${f.key}`} aria-pressed={on} onClick={() => upd({ font: f.key })}
                className={`text-left rounded-xl border p-3.5 transition-colors ${on ? "border-ink bg-ink text-white" : "border-sand-200 bg-white hover:border-ink/40"}`}>
                <div className="text-lg font-bold leading-tight" style={{ fontFamily: f.heading }}>{f.label}</div>
                <div className={`text-sm mt-1 ${on ? "text-white/80" : "text-ink/70"}`} style={{ fontFamily: f.body }}>{f.blurb}</div>
              </button>
            );
          })}
        </div>
        {font && (
          <div className="rounded-xl border border-sand-200 bg-sand-50 px-4 py-3" data-testid="appearance-font-preview">
            <BrandFont brand={{ font_css: font.google, font_heading: font.heading, font_body: font.body }} scope="[data-appearance-preview]" />
            <div data-appearance-preview>
              <div className="font-heading font-bold text-ink text-xl" style={{ fontFamily: font.heading }}>Maplewood Community HOA — 14 open requests</div>
              <div className="text-sm text-ink/70 mt-1" style={{ fontFamily: font.body }}>The concierge answered 128 residents this week; 3 threads are waiting on a human. Board report goes out Friday.</div>
            </div>
          </div>
        )}
      </SettingsSection>

      <SettingsSection id="platform-accent" icon={Palette} title="Console accent" defaultOpen
        badges={[{ text: form.accent || "Redline red", on: !!form.accent }]}
        summary="Buttons, badges and the selected menu item in the company console. Communities still wear their own colour inside their console.">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map(([hex, label]) => (
            <button key={label} type="button" data-testid={`appearance-accent-${label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => upd({ accent: hex })}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${form.accent === hex ? "border-ink" : "border-sand-200 hover:border-ink/40"}`}>
              <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ backgroundColor: hex || "#D32F2F" }} /> {label}
            </button>
          ))}
          <input data-testid="appearance-accent-hex" value={form.accent} onChange={(e) => upd({ accent: e.target.value.trim() })} placeholder="#D32F2F" className={inputCls + " w-32"} />
        </div>
        <div className="flex justify-end">
          <button type="button" data-testid="appearance-save-bottom" onClick={save} disabled={!dirty || saving} className="rounded-full bg-ink text-white px-5 py-2 text-sm font-semibold hover:bg-ink-700 disabled:opacity-40 inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save appearance
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}
